import { isEqualWith } from 'lodash'
import { ResolvedPos } from 'prosemirror-model'
import { Selection } from 'prosemirror-state'
import { Node as ProseNode } from 'prosemirror-model'
import { Mappable } from 'prosemirror-transform'
import { schema } from 'prose-core'

import { Coords, TableMap } from 'common/Prose/utils/table/TableMap'

export type SelectedBorder = {
  rowOrColIdx: number
  fragmentIdx?: number
}
export class BorderSelection extends Selection {
  $tablePos: ResolvedPos
  verticals: SelectedBorder[]
  horizontals: SelectedBorder[]

  constructor($anchor: ResolvedPos, verticals: SelectedBorder[], horizontals: SelectedBorder[]) {
    super($anchor, $anchor)

    this.$tablePos = $anchor
    this.verticals = verticals
    this.horizontals = horizontals

    this.visible = false
  }

  /**
   * Returns all adjacent cell coords for a selected border. Depending on the
   * type the resulting array contains the left/top cell positions first and
   * then the right/bottom ones.
   *
   * @param selectedBorder Any border in the table of this selection
   * @param type The type of that border
   *
   * @returns coordsBySide The coords of the adjacent border: In the case of a
   * horizontal border the first array contains the coordinates of the cells on
   * the top, the second the ones on the bottom. Vertical works analogously.
   * `null` values represent missing cells (consider e.g. the first vertical
   * border of a table; there are no cells on the left)
   */
  getAdjacentCellCoordsForSelectedBorder(
    selectedBorder: SelectedBorder,
    type: 'horizontal' | 'vertical',
  ): [null[], Coords[]] | [Coords[], Coords[]] | [Coords[], null[]] {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const tableMap = new TableMap({ posBeforeTable: this.$tablePos.pos, node: this.$tablePos.nodeAfter! })

    if (type === 'horizontal') {
      if (selectedBorder.fragmentIdx === undefined) {
        if (selectedBorder.rowOrColIdx === 0)
          return [
            Array(tableMap.numCols).fill(null),
            [...Array(tableMap.numCols)].map((_, idx) => [selectedBorder.rowOrColIdx, idx]),
          ]
        else if (selectedBorder.rowOrColIdx === tableMap.numRows)
          return [
            [...Array(tableMap.numCols)].map((_, idx) => [selectedBorder.rowOrColIdx - 1, idx]),
            Array(tableMap.numCols).fill(null),
          ]
        else
          return [
            [...Array(tableMap.numCols)].map((_, idx) => [selectedBorder.rowOrColIdx - 1, idx]),
            [...Array(tableMap.numCols)].map((_, idx) => [selectedBorder.rowOrColIdx, idx]),
          ]
      } else {
        if (selectedBorder.rowOrColIdx === 0)
          return [[null], [[selectedBorder.rowOrColIdx, selectedBorder.fragmentIdx]]]
        else if (selectedBorder.rowOrColIdx === tableMap.numRows)
          return [[[selectedBorder.rowOrColIdx - 1, selectedBorder.fragmentIdx]], [null]]
        else
          return [
            [[selectedBorder.rowOrColIdx - 1, selectedBorder.fragmentIdx]],
            [[selectedBorder.rowOrColIdx, selectedBorder.fragmentIdx]],
          ]
      }
    } else {
      if (selectedBorder.fragmentIdx === undefined) {
        if (selectedBorder.rowOrColIdx === 0)
          return [
            Array(tableMap.numRows).fill(null),
            [...Array(tableMap.numRows)].map((_, idx) => [idx, selectedBorder.rowOrColIdx]),
          ]
        else if (selectedBorder.rowOrColIdx === tableMap.numCols)
          return [
            [...Array(tableMap.numRows)].map((_, idx) => [idx, selectedBorder.rowOrColIdx - 1]),
            Array(tableMap.numRows).fill(null),
          ]
        else
          return [
            [...Array(tableMap.numCols)].map((_, idx) => [idx, selectedBorder.rowOrColIdx - 1]),
            [...Array(tableMap.numCols)].map((_, idx) => [idx, selectedBorder.rowOrColIdx]),
          ]
      } else {
        if (selectedBorder.rowOrColIdx === 0)
          return [[null], [[selectedBorder.fragmentIdx, selectedBorder.rowOrColIdx]]]
        else if (selectedBorder.rowOrColIdx === tableMap.numCols)
          return [[[selectedBorder.fragmentIdx, selectedBorder.rowOrColIdx - 1]], [null]]
        else
          return [
            [[selectedBorder.fragmentIdx, selectedBorder.rowOrColIdx - 1]],
            [[selectedBorder.fragmentIdx, selectedBorder.rowOrColIdx]],
          ]
      }
    }
  }

  getAdjacentCellPosForSelectedBorder(
    selectedBorder: SelectedBorder,
    type: 'horizontal' | 'vertical',
  ): [null[], number[]] | [number[], number[]] | [number[], null[]] {
    const adjacentCellCoords = this.getAdjacentCellCoordsForSelectedBorder(selectedBorder, type)

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const tableMap = new TableMap({ posBeforeTable: this.$tablePos.pos, node: this.$tablePos.nodeAfter! })

    return adjacentCellCoords.map((coords) =>
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      coords.map((coord) => (coord ? tableMap.getPosAtCoord(coord[1], coord[0])! : null)),
    ) as ReturnType<typeof this.getAdjacentCellPosForSelectedBorder>
  }

  eq(other: Selection): boolean {
    return (
      other instanceof BorderSelection &&
      other.$anchor.pos === this.$anchor.pos &&
      isEqualWith(this.verticals, other.verticals) &&
      isEqualWith(this.horizontals, other.horizontals)
    )
  }

  map(doc: ProseNode, mapping: Mappable): Selection {
    let $newAnchor = doc.resolve(mapping.map(this.$anchor.pos))
    return new BorderSelection($newAnchor, this.verticals, this.horizontals)
  }

  toJSON() {
    return {
      type: 'borderSelection',
      anchor: this.$anchor.pos,
      verticals: this.verticals,
      horizontals: this.horizontals,
    }
  }

  getBookmark() {
    return new BorderBookmark(this.$tablePos.pos, this.verticals, this.horizontals)
  }
}

class BorderBookmark {
  tablePos: number
  verticals: SelectedBorder[]
  horizontals: SelectedBorder[]

  constructor(tablePos: number, verticals: SelectedBorder[], horizontals: SelectedBorder[]) {
    this.tablePos = tablePos
    this.verticals = verticals
    this.horizontals = horizontals
  }

  map(mapping: Mappable) {
    return new BorderBookmark(mapping.map(this.tablePos), this.verticals, this.horizontals)
  }
  resolve(doc: ProseNode): Selection {
    let $tablePos = doc.resolve(this.tablePos)
    if ($tablePos.nodeAfter?.type === schema.nodes.table)
      return new BorderSelection($tablePos, this.verticals, this.horizontals)
    else return Selection.near($tablePos, 1)
  }
}

try {
  Selection.jsonID('borderSelection', BorderSelection)
} catch (e) {}
